import { Box, Skeleton, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSingleInsightStart,
  selectInsightById,
  selectInsightsLoading,
} from "../features/insights";
import { DocumentLink } from "./DocumentLink";

// Skeleton for the tooltip content
const TooltipContentSkeleton = () => (
  <>
    <Skeleton variant="text" width="80%" sx={{ mb: 1 }} />
    <Skeleton variant="rectangular" width="100%" height={40} sx={{ mb: 1 }} />
    <Skeleton variant="text" width="60%" />
  </>
);

interface InsightTooltipContentProps {
  insightId: string;
  showDocumentLink?: boolean;
}

export const InsightTooltipContent: React.FC<InsightTooltipContentProps> = ({
  insightId,
  showDocumentLink = true,
}) => {
  console.log("Rendering InsightTooltipContent for ID:", insightId);
  const insight = useSelector(selectInsightById(insightId));
  const loading = useSelector(selectInsightsLoading); // Global loading for any insight
  const dispatch = useDispatch();

  useEffect(() => {
    // Fetch only when the content area is visible, insight is missing, and not already loading
    if (!insight) {
      console.log("Fetching insight:", insightId);
      dispatch(fetchSingleInsightStart(insightId));
    }
  }, []);

  if (loading && !insight) {
    // Show skeleton if loading AND this specific insight isn't available yet
    return <TooltipContentSkeleton />;
  }

  if (!insight) {
    return <Typography variant="caption">Insight not available.</Typography>;
  }

  return (
    <>
      {showDocumentLink && insight && insight.documentId && (
        <Box sx={{ mb: 1.5 }}>
          <DocumentLink documentId={insight.documentId} variant="body1" />
        </Box>
      )}
      <Typography
        variant="body2"
        sx={{ mb: 1.5, color: "rgba(0, 0, 0, 0.87)", lineHeight: 1.5 }}
      >
        {insight.description}
      </Typography>
      {insight.references?.map((ref) => (
        <Typography
          key={ref.pageNumber}
          variant="caption"
          display="block"
          sx={{ color: "rgba(0, 0, 0, 0.6)" }}
        >
          <i>{ref.text}</i> <div>Page {ref.pageNumber}</div>
        </Typography>
      )) ?? null}
    </>
  );
};
